/**
 * FO-153 (batch-32) — gedeelde contracten voor de per-gebruiker module-tab-
 * volgorde (`/me/nav-voorkeuren`).
 *
 * Eén waarheid voor: (a) het request-schema dat de API `.parse`t én de client
 * hergebruikt, en (b) de geldige tab-keys per werkruimte-sleutel — de
 * strip-registry waarmee de PUT onbekende keys STRIPT (niet weigert).
 *
 * Bewust losgekoppeld van de backoffice-render-config `MODULE_TABS`: verlof en
 * acquisitie hebben hier hun VOLLEDIGE (rijkere) tab-set zodat hun bespoke
 * tab-balken persistent blijven, zonder de command-palette/breadcrumb of de
 * /verzuim-weergave te beïnvloeden. Een backoffice-unit-test bewaakt dat elke
 * `MODULE_TABS`-key hier voorkomt (geen drift).
 */

import { z } from 'zod'

/**
 * `soort` is licht generiek opgezet voor latere UI-voorkeuren. Naast de
 * module-tab-volgorde (FO-153) hangt hier sinds FO-340 ook `planbord-afwezigen`
 * aan: welke afwezigen-groepen de planner op zijn planbord heeft uitgeklapt.
 *
 * Let op: de twee soorten delen de tabel en het request-schema, maar NIET de
 * merge-semantiek. Zie `PLANBORD_AFWEZIGEN_SLEUTELS`.
 */
export const NAV_VOORKEUR_SOORTEN = [
  'module-tabs',
  'planbord-afwezigen',
  // batch-49 in-te-plannen (FO-398 §3.1) — welke banden bovenaan het planbord
  // heeft deze planner INGEKLAPT. Zelfde toggle-semantiek als
  // `planbord-afwezigen` (body = volledige nieuwe stand, geen merge).
  'planbord-band',
  // batch-63 werkbon-facturatie (FO-472) — weergave-schakelaars van het
  // planbord: "Toon facturatiestatus" (sleutel `planbord`) en sinds batch-66 /
  // FO-487 "Toon ook niet-ingeplande monteurs" (sleutel `klantweergave`).
  // Toggle-semantiek, geen merge; elke schakelaar zijn eigen rij.
  'planbord-weergave',
  // batch-68 controle (FO-342 §5 / FO-343 §6) - de urennotatie van de controles
  // (decimaal of u:mm), gedeeld door de verlonings- en facturatiecontrole. Toggle-
  // semantiek, geen merge (zie CONTROLE_WEERGAVE_SLEUTELS).
  'controle-weergave',
] as const
export type NavVoorkeurSoort = (typeof NAV_VOORKEUR_SOORTEN)[number]

/**
 * Geldige tab-keys per werkruimte-sleutel. De sleutel is de werkruimte-key
 * (gelijk aan de backoffice `ModuleKey`, plus `acquisitie` en de volledige
 * verlof-set). Verzameling = strip-registry voor de PUT.
 */
export const MODULE_TAB_SLEUTELS = {
  planning: [
    'planbord',
    'werkbonnen',
    'consignatie',
    'dekking',
    // batch-60 vaste-monteur (FO-440 §B.5) — de vervanging-monitor.
    'vervanging-monitor',
    'monteur-inzet',
    'projecten',
  ],
  crm: [
    'relaties',
    'kvk-mutaties',
    // batch-63 pauze-tweeassig (FO-473) — klantoverzicht pauze-afspraken.
    'pauze-instellingen',
    'crm',
    'opvolgingen',
    'tevredenheid',
    'offertes',
    'templates',
    'sla-rapport',
    // batch-69 planbord-afspraak (FO-523 §D.8) — rapportage afspraken met klanten.
    'afspraken',
  ],
  hrm: [
    'medewerkers',
    'hrm',
    'agenda',
    // batch-47 gespreksverslagen (spec 90 §8.3) — HR-overzicht statusverloop.
    'gespreksverslagen',
    // batch-66 contract-mijlpalen (FO-496) — werkvoorraad van de contract-workflow.
    'contractbewaking',
    // batch-67 hr-brieven (FO-506) — HR-overzicht "Besluiten en brieven".
    'besluiten',
    // batch-67 campagnes (FO-501/502/503) — campagne-overzicht + evenementenbeheer.
    'campagnes',
    'evenementen',
    'skill-matrix',
    'matchmaker',
    'onboarding',
    // batch-48 offboarding-cockpit (spec 94 §3.2.6) — de vindbare uitdienst-ingang.
    'offboarding',
    'kandidaten',
    // FO-517 (spec 217) — versieerde STREEP-functiematrix (salarisindicatie).
    'functiematrix',
    'opleidingen',
    'certificaten',
    'bellijst',
    'mutatieverzoeken',
    // batch-71 gesprek-secties (FO-536, FO-537) — de twee HRM-overzichten.
    'ontwikkeling',
    'controlelijsten',
    // batch-71 integrator (FO-534 §C) — het overzicht van de kennismakingen voor de start.
    'kennismakingen',
  ],
  // Verlof: config-set (aanvragen…verzuimkosten) + de bespoke /verlof-tabs
  // (verlofschuld/seniorendagen/vervanging). Union zodat beide balken persisten.
  verlof: [
    'aanvragen',
    'goedkeuren',
    'beoordeling',
    'saldo',
    'verlofschuld',
    'seniorendagen',
    'vervanging',
    'campagnes',
    'verjaardag',
    'verzuim',
    'verzuimkosten',
    // batch-69 tvt-saldi (FO-513 §B4) — opbouwkeuzes TVT/ATV met de dubbel-indicator.
    'opbouwkeuzes',
  ],
  // payroll-werkruimte 18→8 (kickoff-payroll-werkruimte-8-tabs) — 8 hoofdtabs;
  // de subpagina's zitten in de niet-persistente SectieTabs-sub-balken.
  // Vervallen keys in opgeslagen voorkeuren worden door applyTabVolgorde genegeerd.
  // batch-68 controle (FO-342 §12.5) — 'verloningscontrole': de verloningscontrole
  // (stap 1 van de verloningsflow) als eerste tab.
  payroll: [
    'verloningscontrole',
    'uren',
    'verloningsperiode',
    'reservering',
    'payroll',
    'nmbrs',
    'loonstroken',
    'reizen',
    'controle',
  ],
  // FO-406 (batch-51) — 'finqle-verzoeken': de gespiegelde Finqle-facturatieverzoeken.
  // FO-412 (batch-54) — 'akkoord-mails': de werkvoorraad van klant-replies op de
  // goedkeurings-mail (te beoordelen + niet gekoppeld).
  facturen: [
    'facturen',
    'opstellen',
    'finqle-verzoeken',
    'akkoord-mails',
    'inkoop',
    'declaraties',
    'ic',
    'btw',
  ],
  assets: [
    'voertuigen',
    // batch-37 schadeformulier (FO-326) — schademeldingen-overzicht.
    'schade',
    // batch-66 inkoop-afwijkingen (FO-497) — boetes-overzicht.
    'boetes',
    // FO-361 (batch-41) — bijtelling-overzicht + simulator.
    'bijtelling',
    // batch-69 km-balans (FO-515) — werk- en privekilometerbalans.
    'kilometerbalans',
    // batch-70 wagenpark-week (FO-528 + FO-529) — de werkruimte Wagenpark week.
    'wagenpark',
    'uitrusting',
    'gereedschap',
    // FO-310 (batch-34) — karren & koffers. Ontbrak hier sinds de config-tab
    // bestond (drift-guard-test ving het); toegevoegd in batch-41.
    'karren-koffers',
    'abonnementen',
    // Materieel > Garantie - los garantieregister op serienummer.
    'garantie',
    'apparatuur',
    'voorraad',
    // batch-67 middelen-inname (FO-505) — reparatieverzoeken.
    'reparaties',
    'bijtelling-tarieven',
    'instellingen',
  ],
  rapportage: ['operationeel', 'hr', 'ai'],
  // batch-70 wagenpark-week (FO-528 + FO-529) — de sub-tabs van Materieel > Wagenpark week
  // (handgebouwde tab-set onder /materieel/wagenpark, zoals acquisitie: niet in MODULE_TABS).
  wagenpark: ['week', 'weekrapport', 'km-standen', 'contract-km', 'onderhoud', 'tankpassen', 'import', 'databronnen', 'instellingen'],
  calculatie: [
    'dashboard',
    'kostprijs',
    'vergelijk',
    'maken',
    'offertes',
    'planning',
    'historie',
    'projecten',
    'onderhoud',
    'scenarios',
    'instellingen',
  ],
  // batch-71 integrator (FO-533 §F) — 'workflows': de stappenplannen.
  'operationele-taken': ['open-lijst', 'planbord', 'dashboard', 'import', 'templates', 'instellingen', 'workflows'],
  // Acquisitie: bespoke tab-balk (migreert naar het gedeelde component). Keys
  // hier zodat de volgorde persistent is; bewust NIET in MODULE_TABS (zou de
  // command-palette/breadcrumb nieuw laten opduiken — FO §9).
  acquisitie: [
    'dashboard',
    'leads',
    'funnels',
    'kalender',
    'taken',
    // batch-53 acquisitie-vervolg (FO-407 §B) - het kopje "Vervolgcontacten".
    'vervolgcontacten',
    'relaties',
    'rapportages',
    'import',
    'templates',
    'instellingen',
  ],
} as const satisfies Record<string, readonly string[]>

export type ModuleTabSleutel = keyof typeof MODULE_TAB_SLEUTELS

export function isModuleTabSleutel(sleutel: string): sleutel is ModuleTabSleutel {
  return Object.prototype.hasOwnProperty.call(MODULE_TAB_SLEUTELS, sleutel)
}

// ─── FO-340 §10.3 — uitgeklapte afwezigen-groepen op het planbord ────────────
//
// Langdurig zieken en non-actieven staan standaard INGEKLAPT achter een toggle:
// twaalf langdurig zieken bovenaan het raster kosten elke dag scroll-tijd zonder
// ooit relevant te zijn. Wie ze wél wil zien, wil dat meestal elke dag - vandaar
// dat de keuze per gebruiker bewaard blijft i.p.v. elke sessie terug te vallen.
//
// Hergebruikt bewust `UserNavVoorkeur` (soort/sleutel/String[]): het is precies
// hetzelfde soort persoonlijke UI-voorkeur als de tab-volgorde, dus een eigen
// tabel + migratie zou alleen maar een tweede mechanisme opleveren.
//
// De opgeslagen array = de groepen die AAN staan (uitgeklapt). Geen rij / lege
// array = beide ingeklapt (de default).
//
// KRITIEK VERSCHIL met module-tabs: hier geldt `mergeVoorkeurUpdate` NIET. Die
// bewaart keys uit de vorige voorkeur (zodat een verborgen tab terugkeert), maar
// bij een toggle betekent "key weg" juist "uitgezet". Zou je hier mergen, dan
// kreeg de planner een groep die nooit meer dicht gaat.

export const PLANBORD_AFWEZIGEN_SLEUTELS = {
  planbord: ['langdurig-ziek', 'non-actief'],
} as const satisfies Record<string, readonly string[]>

export type PlanbordAfwezigenSleutel = keyof typeof PLANBORD_AFWEZIGEN_SLEUTELS
export type PlanbordAfwezigenGroep = (typeof PLANBORD_AFWEZIGEN_SLEUTELS)['planbord'][number]

/** De enige sleutel vandaag; als constante zodat client en API 'm niet los typen. */
export const PLANBORD_AFWEZIGEN_SLEUTEL: PlanbordAfwezigenSleutel = 'planbord'

export function isPlanbordAfwezigenSleutel(sleutel: string): sleutel is PlanbordAfwezigenSleutel {
  return Object.prototype.hasOwnProperty.call(PLANBORD_AFWEZIGEN_SLEUTELS, sleutel)
}

/** Strip onbekende groep-keys + dedupliceer. Zelfde geest als `stripNavVolgorde`. */
export function stripPlanbordAfwezigen(
  sleutel: PlanbordAfwezigenSleutel,
  groepen: readonly string[],
): PlanbordAfwezigenGroep[] {
  const geldig = new Set<string>(PLANBORD_AFWEZIGEN_SLEUTELS[sleutel])
  const seen = new Set<string>()
  const out: PlanbordAfwezigenGroep[] = []
  for (const key of groepen) {
    if (!geldig.has(key) || seen.has(key)) continue
    seen.add(key)
    out.push(key as PlanbordAfwezigenGroep)
  }
  return out
}

/** De opgeslagen groep-keys → de board-query-toggles. */
export function planbordAfwezigenToggles(groepen: readonly string[]): {
  toonLangdurigZiek: boolean
  toonNonActief: boolean
} {
  return {
    toonLangdurigZiek: groepen.includes('langdurig-ziek'),
    toonNonActief: groepen.includes('non-actief'),
  }
}

// ─── batch-49 in-te-plannen (FO-398 §3.1) — inklap-voorkeur planbord-banden ──
//
// Het vak "In te plannen" staat als band bovenaan het planbord en is default
// OPEN zodra er items zijn (§3.1) - de hele reden van dit FO is dat werk zonder
// uitvoerder onzichtbaar was. Een planner die 'm toch dicht wil, wil dat
// meestal elke dag; die keuze bewaren we per gebruiker.
//
// De opgeslagen array = de banden die INGEKLAPT staan. Geen rij / lege array =
// alles open (de default). Bewust de omgekeerde polariteit van
// `planbord-afwezigen` (daar is de default juist dicht): in beide gevallen is
// "geen voorkeur opgeslagen" de veilige stand.
export const PLANBORD_BAND_SLEUTELS = {
  planbord: ['in-te-plannen'],
} as const satisfies Record<string, readonly string[]>

export type PlanbordBandSleutel = keyof typeof PLANBORD_BAND_SLEUTELS
export type PlanbordBandKey = (typeof PLANBORD_BAND_SLEUTELS)['planbord'][number]

/** De enige sleutel vandaag; als constante zodat client en API 'm niet los typen. */
export const PLANBORD_BAND_SLEUTEL: PlanbordBandSleutel = 'planbord'

export function isPlanbordBandSleutel(sleutel: string): sleutel is PlanbordBandSleutel {
  return Object.prototype.hasOwnProperty.call(PLANBORD_BAND_SLEUTELS, sleutel)
}

/** Strip onbekende band-keys + dedupliceer. Zelfde geest als `stripNavVolgorde`. */
export function stripPlanbordBanden(
  sleutel: PlanbordBandSleutel,
  banden: readonly string[],
): PlanbordBandKey[] {
  const geldig = new Set<string>(PLANBORD_BAND_SLEUTELS[sleutel])
  const seen = new Set<string>()
  const out: PlanbordBandKey[] = []
  for (const key of banden) {
    if (!geldig.has(key) || seen.has(key)) continue
    seen.add(key)
    out.push(key as PlanbordBandKey)
  }
  return out
}

/** De opgeslagen band-keys → "staat het vak In te plannen ingeklapt?". */
export function planbordInTePlannenIngeklapt(banden: readonly string[]): boolean {
  return banden.includes('in-te-plannen')
}

// ─── batch-63 werkbon-facturatie (FO-472) — weergave-schakelaars planbord ─────
//
// De opgeslagen array = de onderdelen die de planner UITGEZET heeft. Geen rij /
// lege array = alles aan (de default): de facturatiestatus is juist bedoeld om
// de achterstand zichtbaar te maken, dus "geen voorkeur" betekent "tonen".
//
// batch-66 planbord-klantfilter (FO-487) - een tweede schakelaar, met een EIGEN
// sleutel (`klantweergave`) en dus een eigen rij: "Toon ook niet-ingeplande
// monteurs" bij een klantfilter. Bewust geen tweede key onder `planbord`: de PUT
// slaat de body op als volledige stand (geen merge), dus twee schakelaars in een
// rij zouden elkaar wissen zodra ze kort na elkaar of vanuit twee tabbladen
// worden omgezet. Met een rij per schakelaar kan dat niet. Opgeslagen =
// `niet-ingepland-tonen` (aan); geen rij / lege array = uit (de default).
export const PLANBORD_WEERGAVE_SLEUTELS = {
  planbord: ['facturatiestatus-uit'],
  klantweergave: ['niet-ingepland-tonen'],
} as const satisfies Record<string, readonly string[]>

export type PlanbordWeergaveSleutel = keyof typeof PLANBORD_WEERGAVE_SLEUTELS
export type PlanbordWeergaveKey = (typeof PLANBORD_WEERGAVE_SLEUTELS)['planbord'][number]
export type PlanbordKlantweergaveKey =
  (typeof PLANBORD_WEERGAVE_SLEUTELS)['klantweergave'][number]

/** De sleutel van de facturatie-schakelaar; als constante zodat client en API 'm niet los typen. */
export const PLANBORD_WEERGAVE_SLEUTEL: PlanbordWeergaveSleutel = 'planbord'
/** batch-66 (FO-487) - de sleutel van "Toon ook niet-ingeplande monteurs". */
export const PLANBORD_KLANTWEERGAVE_SLEUTEL: PlanbordWeergaveSleutel = 'klantweergave'

export function isPlanbordWeergaveSleutel(sleutel: string): sleutel is PlanbordWeergaveSleutel {
  return Object.prototype.hasOwnProperty.call(PLANBORD_WEERGAVE_SLEUTELS, sleutel)
}

/**
 * Strip onbekende weergave-keys + dedupliceer. Zelfde geest als `stripNavVolgorde`.
 * Per sleutel: een key van de ene schakelaar is onbekend onder de andere.
 */
export function stripPlanbordWeergave(
  sleutel: PlanbordWeergaveSleutel,
  keys: readonly string[],
): Array<PlanbordWeergaveKey | PlanbordKlantweergaveKey> {
  const geldig = new Set<string>(PLANBORD_WEERGAVE_SLEUTELS[sleutel])
  const seen = new Set<string>()
  const out: Array<PlanbordWeergaveKey | PlanbordKlantweergaveKey> = []
  for (const key of keys) {
    if (!geldig.has(key) || seen.has(key)) continue
    seen.add(key)
    out.push(key as PlanbordWeergaveKey | PlanbordKlantweergaveKey)
  }
  return out
}

/** De opgeslagen weergave-keys → "toont het bord de facturatiestatus?". */
export function planbordFacturatiestatusZichtbaar(keys: readonly string[]): boolean {
  return !keys.includes('facturatiestatus-uit')
}

/**
 * batch-66 planbord-klantfilter (FO-487) - de opgeslagen keys van de sleutel
 * `klantweergave` → "toont het bord bij een klantfilter ook de niet-ingeplande
 * monteurs?". Default (geen rij / geen key) = nee.
 */
export function planbordToontNietIngepland(keys: readonly string[]): boolean {
  return keys.includes('niet-ingepland-tonen')
}

/** De stand van de schakelaar → de key-lijst voor de PUT op de sleutel `klantweergave`. */
export function planbordKlantweergaveKeys(toonNietIngepland: boolean): PlanbordKlantweergaveKey[] {
  return toonNietIngepland ? ['niet-ingepland-tonen'] : []
}

// ─── batch-68 controle (FO-342 §5) — urennotatie van de controles ────────────
//
// Uren staan app-breed met twee decimalen; in de TVT-controle leest u:mm vaak
// natuurlijker. De keuze geldt per gebruiker voor beide controles. Opgeslagen =
// `['umm']` (u:mm aan); geen rij of een lege array = decimaal (de app-standaard).
// Toggle-semantiek: de body IS de nieuwe stand, geen merge.
export const CONTROLE_WEERGAVE_SLEUTELS = {
  urennotatie: ['umm'],
} as const satisfies Record<string, readonly string[]>

export type ControleWeergaveSleutel = keyof typeof CONTROLE_WEERGAVE_SLEUTELS

/** De enige sleutel vandaag; als constante zodat client en API 'm niet los typen. */
export const CONTROLE_URENNOTATIE_SLEUTEL: ControleWeergaveSleutel = 'urennotatie'

export function isControleWeergaveSleutel(sleutel: string): sleutel is ControleWeergaveSleutel {
  return Object.prototype.hasOwnProperty.call(CONTROLE_WEERGAVE_SLEUTELS, sleutel)
}

/** Strip onbekende keys + dedupliceer. Zelfde geest als `stripNavVolgorde`. */
export function stripControleWeergave(sleutel: ControleWeergaveSleutel, keys: readonly string[]): string[] {
  const geldig = new Set<string>(CONTROLE_WEERGAVE_SLEUTELS[sleutel])
  const seen = new Set<string>()
  const out: string[] = []
  for (const key of keys) {
    if (!geldig.has(key) || seen.has(key)) continue
    seen.add(key)
    out.push(key)
  }
  return out
}

/** De opgeslagen keys -> de urennotatie. Default (geen rij) = decimaal. */
export function controleUrennotatieUitVoorkeur(keys: readonly string[] | null | undefined): 'DECIMAAL' | 'UMM' {
  return keys?.includes('umm') ? 'UMM' : 'DECIMAAL'
}

// Tab-key = slug (kleine letters/cijfers/koppelteken). Bewust streng zodat een
// buggy client geen willekeurige strings kan opslaan; onbekende-maar-geldige
// keys worden vervolgens gestript tegen de sleutel-registry.
const tabKeySchema = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[a-z0-9][a-z0-9-]*$/, 'Ongeldige tab-key')

/** Request-body voor PUT /me/nav-voorkeuren/:soort/:sleutel. */
export const navVolgordeSchema = z.object({
  volgorde: z.array(tabKeySchema).max(50),
})
export type NavVolgordeInput = z.infer<typeof navVolgordeSchema>

export interface NavVoorkeurDTO {
  soort: string
  sleutel: string
  volgorde: string[]
}

/**
 * Strip onbekende tab-keys tegen de config van de werkruimte en dedupliceer,
 * met behoud van volgorde. Onbekende keys worden GENEGEERD (niet geweigerd) —
 * de config kan net gewijzigd zijn.
 */
export function stripNavVolgorde(sleutel: ModuleTabSleutel, volgorde: readonly string[]): string[] {
  const geldig = new Set<string>(MODULE_TAB_SLEUTELS[sleutel])
  const seen = new Set<string>()
  const out: string[] = []
  for (const key of volgorde) {
    if (!geldig.has(key) || seen.has(key)) continue
    seen.add(key)
    out.push(key)
  }
  return out
}
